import { useState, useEffect, useCallback } from 'react'
import Grid from 'shared/components/grid'
import { IGif } from '@giphy/js-types'
import styled from 'styled-components'
import { SectionHeader } from 'shared/components/text-elements'
import * as colors from '@giphy/js-brand'
import { mobileFetch } from 'shared/api'
import { useChannelWithNormalGifFetch } from 'shared/api/channels'
import useWindowSize from 'shared/hooks/window-size'
import AttributionGrid from 'shared/components/sticker-attribution/attribution-grid'
import { Loader } from 'shared/components/preloader-animation'
import log from 'shared/util/log'
import Reactions from './reactions'
import REACTIONS from './reactions-list'

const REACTIONS_ENABLED = true // added this here to make it easy to turn off the reactions feature if need be

const Options = styled.div`
    box-sizing: border-box;
    line-height: 1;
    padding: 8px;
    text-align: left;
    width: 100%;
`

type OptionProps = {
    active: boolean
}

const Option = styled(SectionHeader)<OptionProps>`
    color: ${(props) => (props.active ? colors.giphyWhite : colors.giphyLightCharcoal)};
    display: inline-block;
    margin-right: 12px;
`

const SCREENS = {
    RELATED: 'RELATED',
    REACTIONS: 'REACTIONS',
    ATTRIBUTION: 'ATTRIBUTION',
}

type EnhancedGif = {
    has_attribution: boolean
}

type Props = {
    gif: IGif & EnhancedGif
}

const RelatedGifs = ({ gif }: Props) => {
    const { innerWidth } = useWindowSize()
    const [reaction, setReaction] = useState(REACTIONS[0])
    const [isFetching, setIsFetching] = useState(false)
    const [screen, setScreen] = useState(SCREENS.RELATED)
    const REACTIONS_CHANNEL = 681
    const fetchChannelGifs = useChannelWithNormalGifFetch(REACTIONS_CHANNEL, {
        term: reaction.search,
        isFeaturedTag: true,
    })

    useEffect(() => {
        const ref = document.referrer
        const isFacebook = ref.indexOf('facebook.com') > -1
        // @ts-ignore
        const inTest = window.hasOwnProperty('optimizeReactionsVariant') ? window.optimizeReactionsVariant : null
        if (REACTIONS_ENABLED && inTest && isFacebook) {
            setScreen(SCREENS.REACTIONS)
        }
    }, [])

    const fetchRelatedGifs = useCallback(
        async (offset: number) => {
            setIsFetching(true)
            try {
                const data = await mobileFetch.related(String(gif.id), { offset })
                setIsFetching(false)
                return data
            } catch (e) {
                setIsFetching(false)
                log.error(e)
                throw e
            }
        },
        [gif.id]
    )

    const fetchReactionGifs = useCallback(async (offset: number) => {
        setIsFetching(true)
        try {
            const data = await fetchChannelGifs(offset)
            setIsFetching(false)
            return data
        } catch (e) {
            setIsFetching(false)
            log.error(e)
            throw e
        }
    }, [])

    const handleReactionsOption = useCallback(() => {
        setScreen(SCREENS.REACTIONS)
    }, [])

    const handleRelatedOption = useCallback(() => {
        setScreen(SCREENS.RELATED)
    }, [])
    const handleAttributionsOption = useCallback(() => {
        setScreen(SCREENS.ATTRIBUTION)
    }, [])

    const handleOnReactionClick = useCallback((reaction) => {
        setReaction(reaction)
    }, [])

    const marginTop = gif.user ? 17 : 15

    return (
        <div style={{ marginTop }}>
            <Options>
                {gif.has_attribution && (
                    <Option active={screen === SCREENS.ATTRIBUTION} onClick={handleAttributionsOption}>
                        Stickers Used
                    </Option>
                )}
                <Option active={REACTIONS_ENABLED && screen === SCREENS.RELATED} onClick={handleRelatedOption}>
                    Related
                </Option>
                {REACTIONS_ENABLED && (
                    <Option active={screen === SCREENS.REACTIONS} onClick={handleReactionsOption}>
                        Reactions
                    </Option>
                )}
            </Options>

            {screen === SCREENS.REACTIONS && <Reactions reaction={reaction} onClick={handleOnReactionClick} />}
            {isFetching && <Loader isFetching={isFetching} />}
            {screen === SCREENS.REACTIONS && (
                <Grid
                    key={reaction.search}
                    noRedux={true}
                    width={innerWidth}
                    columns={2}
                    fetchGifs={fetchReactionGifs}
                />
            )}
            {screen === SCREENS.RELATED && (
                <Grid noRedux={true} width={innerWidth} columns={2} fetchGifs={fetchRelatedGifs} />
            )}
            {screen === SCREENS.ATTRIBUTION && gif.has_attribution && (
                <AttributionGrid key={gif.id} columns={2} gif={gif} overrideWidth={innerWidth} />
            )}
        </div>
    )
}

export default RelatedGifs
